import React, { useState, useRef, useEffect } from "react";
import { CheckIcon } from "lucide-react";
import { DateRange } from "react-day-picker";
import { createPortal } from "react-dom";

import { DateFilter } from "./DateFilter";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "app/components/ui/dropdown-menu";
import { toast } from "app/components/ui/use-toast";

interface Props {
  startDate: Date | null;
  endDate: Date | null;
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;
  onApiError?: (error: any) => void;
  headerText?: string;
}

const dateFilterOptions = [
  {
    key: "View All Dates",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
        <g clipPath="url(#clip0_6368_1046)">
          <path d="M7.91667 0.833333H7.5V0.416667C7.5 0.186667 7.31375 0 7.08333 0C6.85292 0 6.66667 0.186667 6.66667 0.416667V0.833333H3.33333V0.416667C3.33333 0.186667 3.14708 0 2.91667 0C2.68625 0 2.5 0.186667 2.5 0.416667V0.833333H2.08333C0.934583 0.833333 0 1.76792 0 2.91667V7.91667C0 9.06542 0.934583 10 2.08333 10H7.91667C9.06542 10 10 9.06542 10 7.91667V2.91667C10 1.76792 9.06542 0.833333 7.91667 0.833333ZM2.08333 1.66667H7.91667C8.60583 1.66667 9.16667 2.2275 9.16667 2.91667V3.33333H0.833333V2.91667C0.833333 2.2275 1.39417 1.66667 2.08333 1.66667ZM7.91667 9.16667H2.08333C1.39417 9.16667 0.833333 8.60583 0.833333 7.91667V4.16667H9.16667V7.91667C9.16667 8.60583 8.60583 9.16667 7.91667 9.16667ZM7.43 5.48C7.59 5.64542 7.585 5.90958 7.41958 6.06917L5.44458 7.9725C5.21417 8.20292 4.9 8.33333 4.56625 8.33333C4.2325 8.33333 3.91833 8.20333 3.6825 7.96708L2.74417 7.09542C2.57583 6.93875 2.56583 6.675 2.7225 6.50667C2.88 6.33792 3.14333 6.32875 3.31125 6.485L4.26042 7.36708C4.44083 7.54625 4.70333 7.53458 4.86 7.37792L6.84083 5.46875C7.00667 5.30917 7.27 5.31417 7.43 5.47958V5.48Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="clip0_6368_1046">
            <rect width="10" height="10" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    ),
  },
  {
    key: "Today",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
        <g clipPath="url(#clip0_6368_9992)">
          <path d="M3.33333 5H2.5C2.04042 5 1.66667 5.37375 1.66667 5.83333V6.66667C1.66667 7.12625 2.04042 7.5 2.5 7.5H3.33333C3.79292 7.5 4.16667 7.12625 4.16667 6.66667V5.83333C4.16667 5.37375 3.79292 5 3.33333 5ZM2.5 6.66667V5.83333H3.33333V6.66667H2.5ZM7.91667 0.833333H7.5V0.416667C7.5 0.186667 7.31375 0 7.08333 0C6.85292 0 6.66667 0.186667 6.66667 0.416667V0.833333H3.33333V0.416667C3.33333 0.186667 3.14708 0 2.91667 0C2.68625 0 2.5 0.186667 2.5 0.416667V0.833333H2.08333C0.934583 0.833333 0 1.76792 0 2.91667V7.91667C0 9.06542 0.934583 10 2.08333 10H7.91667C9.06542 10 10 9.06542 10 7.91667V2.91667C10 1.76792 9.06542 0.833333 7.91667 0.833333ZM2.08333 1.66667H7.91667C8.60583 1.66667 9.16667 2.2275 9.16667 2.91667V3.33333H0.833333V2.91667C0.833333 2.2275 1.39583 1.66667 2.08333 1.66667ZM7.91667 9.16667H2.08333C1.39417 9.16667 0.833333 8.60583 0.833333 7.91667V4.16667H9.16667V7.91667C9.16667 8.60583 8.60583 9.16667 7.91667 9.16667Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="clip0_6368_9992">
            <rect width="10" height="10" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    ),
  },
  {
    key: "Last Week",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
        <g clipPath="url(#clip0_6368_12945)">
          <path d="M7.5 5H2.5C2.04042 5 1.66667 5.37375 1.66667 5.83333V6.66667C1.66667 7.12625 2.04042 7.5 2.5 7.5H7.5C7.95958 7.5 8.33333 7.12625 8.33333 6.66667V5.83333C8.33333 5.37375 7.95958 5 7.5 5ZM2.5 6.66667V5.83333H7.5V6.66667H2.5ZM7.91667 0.833333H7.5V0.416667C7.5 0.186667 7.31375 0 7.08333 0C6.85292 0 6.66667 0.186667 6.66667 0.416667V0.833333H3.33333V0.416667C3.33333 0.186667 3.14708 0 2.91667 0C2.68625 0 2.5 0.186667 2.5 0.416667V0.833333H2.08333C0.934583 0.833333 0 1.76792 0 2.91667V7.91667C0 9.06542 0.934583 10 2.08333 10H7.91667C9.06542 10 10 9.06542 10 7.91667V2.91667C10 1.76792 9.06542 0.833333 7.91667 0.833333ZM2.08333 1.66667H7.91667C8.60583 1.66667 9.16667 2.2275 9.16667 2.91667V3.33333H0.833333V2.91667C0.833333 2.2275 1.39583 1.66667 2.08333 1.66667ZM7.91667 9.16667H2.08333C1.39417 9.16667 0.833333 8.60583 0.833333 7.91667V4.16667H9.16667V7.91667C9.16667 8.60583 8.60583 9.16667 7.91667 9.16667Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="clip0_6368_12945">
            <rect width="10" height="10" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    ),
  },
  {
    key: "Last Month",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
        <g clipPath="url(#clip0_6368_9995)">
          <path d="M7.91667 0.833333H7.5V0.416667C7.5 0.1875 7.3125 0 7.08333 0C6.85417 0 6.66667 0.1875 6.66667 0.416667V0.833333H3.33333V0.416667C3.33333 0.1875 3.14583 0 2.91667 0C2.6875 0 2.5 0.1875 2.5 0.416667V0.833333H2.08333C0.933333 0.833333 0 1.76667 0 2.91667V7.91667C0 9.06667 0.933333 10 2.08333 10H7.91667C9.06667 10 10 9.06667 10 7.91667V2.91667C10 1.76667 9.06667 0.833333 7.91667 0.833333ZM4.58333 3.75V5H3.33333V3.75H4.58333ZM6.66667 3.75V5H5.41667V3.75H6.66667ZM9.16667 3.75V5H7.5V3.75H9.16667ZM2.5 5H0.833333V3.75H2.5V5ZM0.833333 5.83333H2.5V7.08333H0.833333V5.83333ZM3.33333 5.83333H4.58333V7.08333H3.33333V5.83333ZM4.58333 7.91667V9.16667H3.33333V7.91667H4.58333ZM5.41667 7.91667H6.66667V9.16667H5.41667V7.91667ZM5.41667 7.08333V5.83333H6.66667V7.08333H5.41667ZM7.5 5.83333H9.16667V7.08333H7.5V5.83333ZM2.08333 1.66667H7.91667C8.60417 1.66667 9.16667 2.22917 9.16667 2.91667H0.833333C0.833333 2.22917 1.39583 1.66667 2.08333 1.66667ZM0.833333 7.91667H2.5V9.16667H2.08333C1.39583 9.16667 0.833333 8.60417 0.833333 7.91667ZM7.91667 9.16667H7.5V7.91667H9.16667C9.16667 8.60417 8.60417 9.16667 7.91667 9.16667Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="clip0_6368_9995">
            <rect width="10" height="10" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    ),
  },
  {
    key: "Custom Range",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
        <g clipPath="url(#clip0_6368_3582)">
          <path d="M7.08333 4.18292C5.47542 4.18292 4.16667 5.49125 4.16667 7.09958C4.16667 8.69875 5.47542 10 7.08333 10C8.69125 10 10 8.69167 10 7.08333C10 5.48417 8.69125 4.18292 7.08333 4.18292ZM7.08333 9.16667C5.93458 9.16667 5 8.23917 5 7.09958C5 5.95083 5.93458 5.01625 7.08333 5.01625C8.23208 5.01625 9.16667 5.94375 9.16667 7.08333C9.16667 8.23208 8.23208 9.16667 7.08333 9.16667ZM7.79458 7.20542C7.9575 7.36833 7.9575 7.63167 7.79458 7.79458C7.71333 7.87583 7.60667 7.91667 7.5 7.91667C7.39333 7.91667 7.28667 7.87583 7.20542 7.79458L6.78875 7.37792C6.71042 7.29958 6.66667 7.19375 6.66667 7.08333V6.25C6.66667 6.02 6.85292 5.83333 7.08333 5.83333C7.31375 5.83333 7.5 6.02 7.5 6.25V6.91083L7.79458 7.20542ZM10 2.91667V3.75C10 3.98 9.81375 4.16667 9.58333 4.16667C9.35292 4.16667 9.16667 3.98 9.16667 3.75V2.91667C9.16667 2.2275 8.60583 1.66667 7.91667 1.66667H2.08333C1.39417 1.66667 0.833333 2.2275 0.833333 2.91667V3.33333H4.58333C4.81333 3.33333 5 3.52 5 3.75C5 3.98 4.81333 4.16667 4.58333 4.16667H0.833333V7.91667C0.833333 8.60583 1.39417 9.16667 2.08333 9.16667H3.75C3.98 9.16667 4.16667 9.35333 4.16667 9.58333C4.16667 9.81333 3.98 10 3.75 10H2.08333C0.934583 10 0 9.06542 0 7.91667V2.91667C0 1.76792 0.934583 0.833333 2.08333 0.833333H2.5V0.416667C2.5 0.186667 2.68667 0 2.91667 0C3.14667 0 3.33333 0.186667 3.33333 0.416667V0.833333H6.66667V0.416667C6.66667 0.186667 6.85292 0 7.08333 0C7.31375 0 7.5 0.186667 7.5 0.416667V0.833333H7.91667C9.06542 0.833333 10 1.76792 10 2.91667Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="clip0_6368_3582">
            <rect width="10" height="10" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    ),
  },
];

type DateFilterOption = typeof dateFilterOptions[number]['key'];

export const DateFilterDropdown = ({ startDate, endDate, setStartDate, setEndDate, onApiError, headerText = "Req. Date" } : Props) => {
  const [selectedDateFilterOption, setSelectedDateFilterOption] = useState<DateFilterOption>("View All Dates");
  const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
  const [isCustomRangeOpen, setIsCustomRangeOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const dateFilterRef = useRef<HTMLDivElement>(null);

  // Function to determine which option should be selected based on actual dates
  const determineSelectedOption = (start: Date | null, end: Date | null): DateFilterOption => {
    if (!start || !end) return "View All Dates";
    
    const today = new Date();
    const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const endOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
    
    // Check if it's today
    if (startOfDay(start).getTime() === startOfDay(today).getTime() && 
        endOfDay(end).getTime() === endOfDay(today).getTime()) {
      return "Today";
    }
    
    // Check if it's last week (previous Sunday to Saturday)
    const currentDay = today.getDay();
    const daysFromLastSunday = currentDay + 7;
    const lastWeekStart = new Date(today);
    lastWeekStart.setDate(today.getDate() - daysFromLastSunday);
    lastWeekStart.setHours(0, 0, 0, 0);
    
    const lastWeekEnd = new Date(lastWeekStart);
    lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
    lastWeekEnd.setHours(23, 59, 59, 999);
    
    if (startOfDay(start).getTime() === startOfDay(lastWeekStart).getTime() && 
        endOfDay(end).getTime() === endOfDay(lastWeekEnd).getTime()) {
      return "Last Week";
    }
    
    // Check if it's last month
    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    lastMonthStart.setHours(0, 0, 0, 0);
    
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    lastMonthEnd.setHours(23, 59, 59, 999);
    
    if (startOfDay(start).getTime() === startOfDay(lastMonthStart).getTime() && 
        endOfDay(end).getTime() === endOfDay(lastMonthEnd).getTime()) {
      return "Last Month";
    }
    
    // If none of the above, it's a custom range
    return "Custom Range";
  };

  // Sync selectedDateFilterOption with actual dates whenever they change
  useEffect(() => {
    const newSelectedOption = determineSelectedOption(startDate, endDate);
    setSelectedDateFilterOption(newSelectedOption);
  }, [startDate, endDate]);

  const updateDropdownPosition = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 2,
        left: rect.left + window.scrollX
      });
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Check if click is on dropdown trigger
      if (triggerRef.current && triggerRef.current.contains(target)) {
        return;
      }
      
      // Check if click is on dropdown content (options)
      const dropdownContent = document.querySelector('[data-radix-popper-content-wrapper]');
      if (dropdownContent && dropdownContent.contains(target)) {
        return;
      }
      
      // Check if click is on DateFilter
      if (dateFilterRef.current && dateFilterRef.current.contains(target)) {
        return;
      }
      
      // If we reach here, it's a genuine outside click
      setIsDateFilterOpen(false);
      setIsCustomRangeOpen(false);
    };

    if (isDateFilterOpen || isCustomRangeOpen) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true);
      }, 0);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [isDateFilterOpen, isCustomRangeOpen]);

  useEffect(() => {
    if (isCustomRangeOpen) {
      updateDropdownPosition();
      window.addEventListener('scroll', updateDropdownPosition);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isCustomRangeOpen]);

  const handleDateFilterOptionSelect = (option: string, event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    setSelectedDateFilterOption(option as DateFilterOption);
    const today = new Date();
    
    if (option === "View All Dates") {
      setStartDate(null);
      setEndDate(null);
      setIsDateFilterOpen(false);
      setIsCustomRangeOpen(false);
    } else if (option === "Today") {
      setStartDate(today);
      setEndDate(today);
      setIsDateFilterOpen(false);
      setIsCustomRangeOpen(false);
    } else if (option === "Last Week") {
      // Get the previous Sunday to Saturday week
      const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const daysFromLastSunday = currentDay + 7; // Days to go back to last Sunday
      
      const lastWeekStart = new Date(today);
      lastWeekStart.setDate(today.getDate() - daysFromLastSunday);
      lastWeekStart.setHours(0, 0, 0, 0);
      
      const lastWeekEnd = new Date(lastWeekStart);
      lastWeekEnd.setDate(lastWeekStart.getDate() + 6); // Saturday
      lastWeekEnd.setHours(23, 59, 59, 999);
      
      setStartDate(lastWeekStart);
      setEndDate(lastWeekEnd);
      setIsDateFilterOpen(false);
      setIsCustomRangeOpen(false);
    } else if (option === "Last Month") {
      // Get the entire previous month (1st to last day)
      const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      lastMonthStart.setHours(0, 0, 0, 0);
      
      const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0); // Last day of previous month
      lastMonthEnd.setHours(23, 59, 59, 999);
      
      setStartDate(lastMonthStart);
      setEndDate(lastMonthEnd);
      setIsDateFilterOpen(false);
      setIsCustomRangeOpen(false);
    } else if (option === "Custom Range") {
      setIsCustomRangeOpen(true);
      setIsDateFilterOpen(true);
      updateDropdownPosition();
    }
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {

    if (range) {
      setStartDate(range.from || null);
      setEndDate(range.to || null);
      // Keep the DateFilter open for further interaction
    } else {
      setStartDate(null);
      setEndDate(null);
      setSelectedDateFilterOption('View All Dates');
      setIsCustomRangeOpen(false);
      setIsDateFilterOpen(false);
    }
  };

  const handleDateConfirm = (startDate: Date | null, endDate: Date | null) => {
    // Validate date range before applying
    if (startDate && endDate && startDate > endDate) {
      toast({
        title: "Invalid Date Range",
        description: "Start date cannot be greater than end date. Please select a valid date range.",
        variant: "destructive",
      });
      return;
    }

    setStartDate(startDate);
    setEndDate(endDate);
    setIsCustomRangeOpen(false); // Close only the DateFilter
    // Keep isDateFilterOpen true so the dropdown stays open
  };

  const shouldShowActiveBackground = () => {
    if (isDateFilterOpen) return true;
    
    if (selectedDateFilterOption !== "View All Dates") {
      if (selectedDateFilterOption === "Custom Range") {
        return startDate && endDate;
      }
      return true;
    }
    
    return false;
  };

  return (
    <>
      <div className="flex relative gap-2 items-center justify-center">
        <DropdownMenu 
          open={isDateFilterOpen} 
          onOpenChange={(open) => { 
            // Only allow closing if not custom range or if explicitly closing
            if (!open && !isCustomRangeOpen) {
              setIsDateFilterOpen(false);
            } else if (open) {
              setIsDateFilterOpen(true);
            }
          }}
        >
          <DropdownMenuTrigger asChild>
            <div 
              ref={triggerRef}
              className={`relative flex justify-center items-center hover:bg-[#F2F4F8] py-1 rounded cursor-pointer ${shouldShowActiveBackground() ? 'bg-[#F2F4F8]' : ''} ${startDate && endDate ? 'gap-1' : 'gap-1'}`}
            >
              {(startDate && endDate) && (
                <svg xmlns="http://www.w3.org/2000/svg" width="7" height="6" viewBox="0 0 7 6" fill="none">
                  <circle cx="3.5" cy="3" r="3" fill="#55B494"/>
                </svg>
              )}
              <h3 className="capitalize text-[#343A3F] text-xs font-normal leading-[0.9rem] tracking-[-0.0075rem] whitespace-nowrap">
                {headerText}
              </h3>
              <img src="images/chevron-down.svg" alt="" className="mt-px" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent 
            className="bg-white min-w-[7.125rem] max-w-[7.125rem] p-0 border-0 rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
            align="start"
            sideOffset={2}
          >
            {dateFilterOptions.map(({ key, icon: Icon }) => (
              <div
                key={key}
                className={`min-h-6 max-h-[3.4375rem] px-2 py-1.5 cursor-pointer group ${selectedDateFilterOption === key ? 'bg-[#F2F4F8] hover:bg-[#F2F4F8] text-[#5B73F1] hover:text-[#5B73F1]' : 'hover:bg-[#F3F3F3] text-[#343A3F]'}`}
                onClick={(e) => handleDateFilterOptionSelect(key, e)}
              >
                <div className="flex gap-1.5 items-center">
                  {/* <span className="size-[0.625rem] flex-shrink-0">{Icon}</span> */}
                  <div className={`flex-shrink-0 flex items-center justify-center size-[0.5625rem] border border-[#343A3F] rounded-[0.0625rem] ${selectedDateFilterOption === key ? 'bg-[#5B73F1]' : ''}`}>
                    {selectedDateFilterOption === key && (
                      <CheckIcon className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <span className="text-[#343A3F] text-[0.625rem] font-normal leading-3 tracking-[-0.00625rem]">{key}</span>
                </div>
                {selectedDateFilterOption === key && key === "Custom Range" && startDate && endDate && (
                  <div className="space-y-0.5 mt-1">
                    <div className="flex justify-between items-center">
                      <span className="text-[#697077] text-[0.4375rem] font-normal leading-[0.6125rem] tracking-[-0.00438rem]">
                        Start Date -
                      </span>
                      <span className="text-[#697077] text-[0.5625rem] font-normal leading-[0.5625rem]">
                        {`${((startDate?.getMonth() || 0) + 1).toString().padStart(2, '0')} / ${startDate?.getDate().toString().padStart(2, '0')} / ${startDate?.getFullYear().toString().slice(-2)}`}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-[#697077] text-[0.4375rem] font-normal leading-[0.6125rem] tracking-[-0.00438rem]">
                        End Date -
                      </span>
                      <span className="text-[0.5625rem] font-normal leading-[0.5625rem]">
                        {`${((endDate?.getMonth() || 0) + 1).toString().padStart(2, '0')} / ${endDate?.getDate().toString().padStart(2, '0')} / ${endDate?.getFullYear().toString().slice(-2)}`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* DateFilter as portal to avoid sticky header issues */}
      {isCustomRangeOpen && createPortal(
        <div 
          ref={dateFilterRef}
          className="fixed z-10 w-[15.0625rem] rounded shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)] overflow-hidden"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left + 116}px`, // Position to the right of dropdown
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <DateFilter 
            isOpen={isCustomRangeOpen}
            setIsOpen={setIsCustomRangeOpen}
            startDate={startDate}
            endDate={endDate}
            onDateRangeChange={handleDateRangeChange}
            onConfirm={handleDateConfirm}
          />
        </div>,
        document.body
      )}
    </>
  );
};
